import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, Loader2, Copy, Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ActionBarButton } from "@/components/ActionBar";
import { InfoTip } from "@/components/InfoTip";
import { renderSummary } from "@/lib/renderSummary";
import { useL } from "@/lib/useL";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LEVELS = [
  { key: "primaire", fr: "Primaire", en: "Primary" },
  { key: "college", fr: "Collège", en: "Middle school" },
  { key: "lycee", fr: "Lycée", en: "High school" },
];
const MAX_SOURCE = 20000;

// A4 portrait: the 3 versions stacked as bands, separated by dashed cut lines.
function printBands(out, title, L) {
  const w = window.open("", "_blank");
  if (!w) return toast.error(L("Autorisez les fenêtres pour imprimer.", "Allow pop-ups to print."));
  const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]);
  const bands = LEVELS.filter((lv) => out?.[lv.key]).map((lv) => `
    <section class="band"><div class="lvl">${L(lv.fr, lv.en)}</div><div class="ttl">${esc(title)}</div>${renderSummary(out[lv.key])}</section>`)
    .join('<div class="cut">✂ - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -</div>');
  w.document.write(`<html><head><title>SUX1 · 3 niveaux</title><style>
    @page { size: A4 portrait; margin: 10mm; }
    body { font-family: Arial, sans-serif; font-size: 10.5pt; line-height: 1.35; color: #0d0d0d; margin: 0; }
    .band { border: 1.5px solid #002FA7; padding: 8px 12px; page-break-inside: avoid; }
    .lvl { display: inline-block; background: #002FA7; color: #fff; font-weight: 700; font-size: 9pt; letter-spacing: .12em; text-transform: uppercase; padding: 2px 8px; }
    .ttl { font-weight: 700; margin: 4px 0 2px; }
    h1, h2, h3 { font-size: 11pt; margin: 6px 0 2px; } p, ul { margin: 2px 0; } ul { padding-left: 18px; }
    .cut { color: #888; font-size: 9pt; margin: 6px 0; white-space: nowrap; overflow: hidden; }
    .foot { margin-top: 6px; font-size: 8pt; color: #888; text-align: right; }
  </style></head><body>${bands}<div class="foot">sux1.app</div></body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
}

// Same source summarised for 3 class levels side by side (Gemini, 1 credit each).
export function TripleLevels({ source, fallback, config }) {
  const L = useL();
  const navigate = useNavigate();
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const text = source && source.length <= MAX_SOURCE ? source : fallback;
  if (!text) return null;
  const run = async () => {
    setBusy(true);
    setOut({});
    setOpen(true);
    for (const lv of LEVELS) {
      try {
        const { data } = await api.post("/summarize", { ...config, text, model: "gemini-3-flash", audience_level: lv.key, source_label: `${config.title || "SUX1"} (${lv.fr})` });
        setOut((o) => ({ ...o, [lv.key]: data.summary }));
      } catch (err) {
        const st = err?.response?.status;
        setOut((o) => ({ ...o, [lv.key]: false }));
        if (st === 402) {
          toast.error(L("Crédits insuffisants pour les 3 niveaux.", "Not enough credits for the 3 levels."), { action: { label: L("Recharger", "Top up"), onClick: () => navigate("/billing/upgrade") } });
          break;
        }
        toast.error(err?.response?.data?.detail || L("Une version n'a pas pu être créée.", "One version could not be created."));
      }
    }
    setBusy(false);
  };
  const copy = (s) => navigator.clipboard?.writeText(s).then(() => toast.success(L("Copié.", "Copied.")), () => {});
  return (
    <div className="mt-8 max-w-3xl" data-testid="triple-levels">
      <ActionBarButton icon={busy ? Loader2 : Layers} label={busy ? L("Création des 3 niveaux…", "Creating 3 levels…") : L("3 niveaux d'un coup", "3 levels at once")}
        onClick={run} disabled={busy} chevron={false} testid="triple-levels-btn"
        help={L(<><b>3 niveaux d'un coup</b> : SUX1 refait ce résumé pour le Primaire, le Collège et le Lycée, affichés côte à côte. Idéal pour différencier en classe. 1 crédit par niveau (3 au total) ; une version qui échoue n'est pas débitée. Impression A4 en 3 bandes à découper.</>, <><b>3 levels at once</b>: SUX1 rewrites this summary for Primary, Middle and High school, side by side. Ideal for differentiated teaching. 1 credit per level (3 in total); a failed version is not charged. A4 printing in 3 cut-out bands.</>)}
        tip={L("Le même résumé en versions Primaire, Collège et Lycée, côte à côte. 3 crédits.", "The same summary for Primary, Middle and High school, side by side. 3 credits.")} />
      <p className="mt-2 text-center text-[11px] text-muted-foreground">
        {L("Primaire · Collège · Lycée côte à côte", "Primary · Middle · High school side by side")} · <b>{L("3 crédits", "3 credits")}</b>
      </p>
      {out && !busy && (
        <div className="mt-2 flex justify-center">
          <button type="button" onClick={() => setOpen(true)} data-testid="triple-levels-reopen-btn" className="font-mono uppercase tracking-[0.14em] text-[11px] text-[#002FA7] dark:text-blue-300 underline">
            {L("Revoir les 3 niveaux", "Show the 3 levels again")}
          </button>
        </div>
      )}
      <Dialog open={open && !!out} onOpenChange={setOpen}>
        <DialogContent className="max-w-7xl w-[96vw] max-h-[92vh] overflow-auto" data-testid="triple-levels-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>{L("Le même résumé, 3 niveaux de classe", "The same summary, 3 class levels")}</DialogTitle></DialogHeader>
          {!busy && out && Object.values(out).some(Boolean) && (
            <div className="flex justify-end items-center gap-1">
              <button type="button" onClick={() => printBands(out, config.title, L)} data-testid="triple-levels-print-btn"
                className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-[#002FA7] text-[#002FA7] dark:text-blue-300 hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2">
                <Printer className="w-3.5 h-3.5" /> {L("Imprimer la fiche A4 (3 bandes à découper)", "Print A4 sheet (3 cut-out bands)")}
              </button>
              <InfoTip testid="triple-levels-print-info" content={L("Une page A4 avec les 3 versions l'une sous l'autre, séparées par des pointillés : découpez et distribuez chaque bande au groupe concerné.", "One A4 page with the 3 versions stacked, separated by dashed lines: cut and hand each band to the right group.")} />
            </div>
          )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="triple-levels-grid">
          {LEVELS.map((lv) => (
            <div key={lv.key} className="border border-border bg-card p-4 min-w-0" data-testid={`triple-level-${lv.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono uppercase tracking-[0.16em] text-[11px] font-semibold text-[#002FA7] dark:text-blue-300">{L(lv.fr, lv.en)}</span>
                {out?.[lv.key] && (
                  <button type="button" onClick={() => copy(out?.[lv.key])} aria-label={L("Copier", "Copy")} data-testid={`triple-level-${lv.key}-copy`} className="text-muted-foreground hover:text-[#002FA7]"><Copy className="w-3.5 h-3.5" /></button>
                )}
              </div>
              {out?.[lv.key] === undefined && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {L("En cours…", "In progress…")}</div>}
              {out?.[lv.key] === false && <div className="text-sm text-[#FF2A00]">{L("Non créé (aucun crédit débité).", "Not created (no credit charged).")}</div>}
              {out?.[lv.key] && <div className="prose prose-sm max-w-none dark:prose-invert text-[14px]" dangerouslySetInnerHTML={{ __html: renderSummary(out?.[lv.key]) }} />}
            </div>
          ))}
        </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TripleLevels;
